import React, { useState, useEffect, useRef, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useApiCall } from "../custom-hooks/api-call";
import { GlobalContext } from "../../context/Context";
import moment from "moment";
import { Paperclip } from "lucide-react";

const CommentsSection = ({
  type,
  id,
  apiBaseUrl,
  setAlertData,
  title,
}) => {
  const { state } = useContext(GlobalContext);
  const loginUserId = state?.user?.userid;
  const apiCall = useApiCall();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const commentsEndRef = useRef(null);

  // console.log(id);
  // console.log("apiBaseUrl", apiBaseUrl);

  // Fetch comments
  const GetComments = async () => {
    if (!id) return;
    try {
      setIsCommentLoading(true);
      const res = await apiCall({
        method: "get",
        url: `${apiBaseUrl}/get-${apiBaseUrl}-comments/${type}/${id}`,
      });
      setComments(res?.comments || []);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsCommentLoading(false);
    }
  };

  // ✅ Upload files
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file));

      const uploadRes = await apiCall({
        method: "post",
        url: `attachments/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (Array.isArray(uploadRes?.data)) {
        const uploaded = uploadRes.data.map((f) => ({
          id: f.id,
          file_url: f.file_url,
          status: f.status,
          file_name: f.file_name,
          uploaded_by: loginUserId,
        }));
        setAttachments((prev) => [...prev, ...uploaded]);
      }
    } catch (err) {
      console.error("File upload error:", err);
      setAlertData?.({
        isShow: true,
        type: "error",
        message: "Error uploading files",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // ✅ Add comment
  const AddComment = async () => {
    if (!newComment.trim() && attachments.length === 0) return;

    try {
      const res = await apiCall({
        method: "post",
        url: `${apiBaseUrl}/add-${apiBaseUrl}-comment`,
        data: {
          [`${type}_id`]: id,
          comment: newComment.trim(),
          attachment_ids: attachments.map((a) => a.id),
        },
      });

      if (res?.status === true) {
        setNewComment("");
        setAttachments([]);
        await GetComments();

        setAlertData?.({
          isShow: true,
          type: "success",
          message: res?.message || "Comment added successfully",
        });
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setAlertData?.({
        isShow: true,
        type: "error",
        message: "Failed to add comment",
      });
    }
  };

  // ✅ Delete attachment (confirmed)
  // const handleDeleteAttachment = async (fileId, commentId) => {
  //   try {
  //     // Optimistic UI update
  //     setComments((prev) =>
  //       prev.map((c) =>
  //         c.id === commentId
  //           ? {
  //             ...c,
  //             attachments: c.attachments.filter((a) => a.id !== fileId),
  //           }
  //           : c
  //       )
  //     );

  //     const res = await apiCall({
  //       method: "post",
  //       url: `attachments/delete/${fileId}`,
  //     });

  //     if (res?.status === true) {
  //       setAlertData?.({
  //         isShow: true,
  //         type: "success",
  //         message: "Attachment deleted successfully",
  //       });
  //     }
  //   } catch (err) {
  //     console.error("Error deleting attachment:", err);
  //     setAlertData?.({
  //       isShow: true,
  //       type: "error",
  //       message: "Error deleting attachment",
  //     });
  //   }
  // };

  // ✅ Scroll on update
  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments]);

  // ✅ Load comments on open
  useEffect(() => {
    if (id) GetComments();
  }, [id]);

  return (
    <div className="relative flex flex-col border border-slate-200 bg-white rounded-2xl shadow-sm overflow-hidden h-full">
      {title ? (
        <div className="flex gap-1 p-3 items-center">
          <h2 className="font-medium text-[22px]">Activity Logs & Comments</h2>
        </div>
      ) : null}

      {/* --- Comments List --- */}
      <div className="flex-1 flex flex-col gap-5 p-6 py-0 max-h-full overflow-y-auto scroll-smooth">
        {isCommentLoading ? (
          <div className="text-sm text-slate-500 text-center py-6">
            Loading comments...
          </div>
        ) : comments.length > 0 ? (
          <AnimatePresence initial={false}>
            <div className="flex-1 overflow-y-auto pr-1 space-y-6 relative h-full">
              {/* <div className="absolute left-[15px] top-[32px] bottom-[32px] w-[1.5px]  bg-gray-200"></div> */}
              {comments
                .slice()
                .reverse()
                .map((c, idx) => {
                  const commentDate = new Date(c.comment_timestamp);
                  const hasAttachments =
                    Array.isArray(c.attachments) && c.attachments.length > 0;

                  return (
                    <motion.div
                      key={c.id || idx}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.25 }}
                      // className="flex items-start gap-3"
                      className="flex items-start gap-3 relative z-10"
                    >
                      {/* --- Avatar --- */}
                      <div className="relative">
                        <div className="w-8 h-8  flex items-center justify-center rounded-full cursor-pointer border border-gray-300 transition-all  bg-slate-200  overflow-hidden">
                          {c.user?.userimg ? (
                            <img
                              src={c.user.userimg}
                              alt={c.user?.name || "User"}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  c.user?.name || "User"
                                )}`;
                              }}
                            />
                          ) : (
                            <p className="capitalize font-semibold text-base text-black">
                              {(c.user?.name?.split(" ") || ["U"])
                                .map((n) => n.charAt(0))
                                .join("")}
                            </p>
                          )}
                          {comments?.length - 1 == idx ? null : (
                            <div
                              className={`absolute left-[15px] top-[32px] bottom-[32px] w-[1.5px]  ${
                                c?.attachments?.length > 0
                                  ? "h-[calc(100%+68px)]"
                                  : "h-[calc(100%+24px)]"
                              }  bg-gray-200`}
                            ></div>
                          )}
                        </div>
                      </div>

                      {/* Comment Card */}
                      <div className="flex-1  rounded-lg pt-2 cursor-pointer transition-all">
                        <div className="flex justify-between items-center">
                          <p className="text-[13px] font-medium text-black flex items-center gap-2">
                            {c?.user?.name}{" "}
                            <span className="text-[12px] text-gray-500 pl-1">
                              {/* {console.log(
                                "Raw:",
                                c.created_at,
                                "Parsed:",
                                moment(c.created_at, "DD-MMM-YYYY").format()
                              )} */}
                              {/* {moment(
                                c?.created_at?.toUpperCase().toString().trim(),
                                "DD-MMM-YYYY"
                              ).fromNow()} */}
                              {moment(
                                c?.comment_timestamp,
                                "DD-MMM-YYYY hh:mm:ss A"
                              ).fromNow()}
                            </span>
                          </p>
                        </div>

                        <p className="text-[15px] text-gray-700 mt-3 leading-snug">
                          {c?.comment}
                        </p>

                        {/* --- Attachments --- */}
                        {hasAttachments && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {c.attachments.map((file, i) => {
                              const fileUrl = file.file_url;
                              const fileName = file.file_name || "Attachment";
                              const ext = fileName
                                .split(".")
                                .pop()
                                .toLowerCase();
                              const isImage = [
                                "jpg",
                                "jpeg",
                                "png",
                                "gif",
                                "webp",
                              ].includes(ext);
                              const iconMap = {
                                pdf: "📄",
                                doc: "📝",
                                docx: "📝",
                                xls: "📊",
                                xlsx: "📊",
                                csv: "📈",
                                txt: "📘",
                                zip: "🗜️",
                                rar: "🗜️",
                              };
                              const fileIcon = iconMap[ext] || "📎";

                              return (
                                <div
                                  key={i}
                                  className="group flex items-center gap-2 border border-slate-200 bg-white hover:bg-slate-100 rounded-lg px-3 py-1 text-sm shadow-sm transition relative"
                                >
                                  <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2"
                                  >
                                    {isImage ? (
                                      <img
                                        src={fileUrl}
                                        alt={fileName}
                                        className="w-8 h-8 object-cover rounded-md border border-slate-200"
                                      />
                                    ) : (
                                      <div className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-lg">
                                        {fileIcon}
                                      </div>
                                    )}
                                    <span className="truncate max-w-[160px] text-slate-700 group-hover:underline">
                                      {fileName}
                                    </span>
                                  </a>

                                  {/* {file.uploaded_by === loginUserId && (
                                  <button
                                    onClick={() => handleDeleteAttachment(file.id, c.id)}
                                    className="absolute -top-2 -right-2 bg-white border border-slate-300 text-slate-400 hover:text-red-500 rounded-full w-5 h-5 flex items-center justify-center shadow-sm text-xs"
                                    title="Delete Attachment"
                                  >
                                    ✕
                                  </button>
                                )} */}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </AnimatePresence>
        ) : (
          <div className="text-center py-10">
            <p className="text-sm text-slate-500">💬 No comments yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Be the first to share an update!
            </p>
          </div>
        )}
        <div ref={commentsEndRef} />
      </div>

      {/* --- Input Section --- */}
      <div className="px-5 p-2 !w-full mb-1">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
           
              <div className="w-8 h-8  flex items-center justify-center rounded-full cursor-pointer border border-gray-300 transition-all  bg-slate-200  overflow-hidden">
                <p className="capitalize  text-sm text-black">
                  {(state?.user?.name?.split(" ") || ["U"])
                    .map((n) => n.charAt(0))
                    .join("")}
                </p>
              </div>
           

            {/* Comment Input */}
            {/* <textarea
              placeholder="Add a comment..."
              className="flex-1  resize-non  min-h-[18px] rounded pt-1 px-[2px] pb-0  text-sm text-slate-700 placeholder:text-slate-400 outline-none shadow-lg"
              rows={2}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            /> */}

            <input 
            type="text" 
            placeholder="Add a comment..."
              className="flex-1  resize-non  min-h-[18px] rounded px-2 py-[6px]    text-sm text-slate-700 placeholder:text-slate-400 outline-none shadow-lg"
              rows={2}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              />

            {/* Send Button */}
            {/* <button
              onClick={AddComment}
              disabled={!newComment.trim() && attachments.length === 0}
              className="flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-slate-800 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              {uploading ? "Uploading..." : "Send"}
            </button> */}

            <div className="flex items-center gap-2">
             
              <div className="text-black font-normal leading-normal text-sm cursor-pointer">
                @
              </div>
              <div className="relative">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  id={`comment-attachments-${type}`}
                  className="hidden"
                />
                <label
                  htmlFor={`comment-attachments-${type}`}
                  className={`cursor-pointer flex items-center justify-center ${
                    uploading ? "opacity-50 cursor-wait" : ""
                  }`}
                  title="Attach files"
                >
                  {/* <Paperclip size={20} /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 13 13"
                    fill="none"
                  >
                    <path
                      d="M11.7828 1.21521C10.1625 -0.405071 7.52602 -0.405071 5.90568 1.21521L0.867889 6.25293C-0.289368 7.41034 -0.289292 9.29357 0.868092 10.4509C1.44682 11.0296 2.20677 11.319 2.96702 11.3189C3.72706 11.3188 4.48736 11.0296 5.06601 10.4509L9.68382 5.83305C10.3783 5.13871 10.3784 4.00877 9.6839 3.31419C8.98941 2.61978 7.85942 2.6198 7.16509 3.31431L4.14525 6.33405C3.9134 6.56587 3.9134 6.94178 4.1452 7.17365C4.37709 7.40549 4.75292 7.40549 4.98482 7.17365L8.00471 4.15383C8.23617 3.92234 8.61284 3.92234 8.8443 4.15375C9.07577 4.38529 9.07577 4.76189 8.84423 4.99337L4.22647 9.61131C3.53198 10.3057 2.4021 10.3058 1.70766 9.61136C1.01325 8.9169 1.01317 7.78696 1.70751 7.0925L6.74528 2.05481C7.90266 0.897524 9.78587 0.897473 10.9433 2.05486C11.5039 2.61548 11.8127 3.36093 11.8127 4.15381C11.8127 4.94671 11.5039 5.69213 10.9432 6.2528L5.90556 11.2906C5.67372 11.5224 5.67372 11.8983 5.90561 12.1302C6.02154 12.2461 6.17348 12.3041 6.32539 12.3041C6.47731 12.3041 6.62927 12.2461 6.7452 12.1302L11.7827 7.09245C12.5677 6.30755 13 5.26394 13 4.15386C13.0001 3.04372 12.5678 2.00011 11.7828 1.21521Z"
                      fill="black"
                    />
                  </svg>
                </label>
              </div>
              <button
                onClick={AddComment}
                disabled={!newComment.trim() && attachments?.length === 0}
                className={
                  uploading ? "opacity-50 cursor-wait" : "cursor-pointer"
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 13 13"
                  fill="none"
                >
                  <path
                    d="M9.19141 0H3.80859C1.70625 0 0 1.70625 0 3.80859V9.19141C0 11.2937 1.70625 13 3.80859 13H9.19141C11.2937 13 13 11.2937 13 9.19141V3.80859C13 1.70625 11.2937 0 9.19141 0ZM11.7305 9.19141C11.7305 10.5914 10.5914 11.7305 9.19141 11.7305H3.80859C2.40855 11.7305 1.26953 10.5914 1.26953 9.19141V3.80859C1.26953 2.40855 2.40855 1.26953 3.80859 1.26953H9.19141C10.5914 1.26953 11.7305 2.40855 11.7305 3.80859V9.19141ZM10.3721 7.85078L8.85396 9.36889C8.61402 9.60883 8.20371 9.43896 8.20371 9.09949V8.39389H2.89732C2.68709 8.39389 2.51646 8.22326 2.51646 8.01303V3.89975C2.51646 3.68951 2.68709 3.51889 2.89732 3.51889H5.38561C5.59584 3.51889 5.76646 3.68951 5.76646 3.89975V4.76303C5.76646 4.97326 5.59584 5.14389 5.38561 5.14389H4.14146V6.76889H8.20396V6.06328C8.20396 5.72406 8.61428 5.55395 8.85422 5.79389L10.3723 7.31199C10.5211 7.46078 10.5211 7.70199 10.3723 7.85053L10.3721 7.85078Z"
                    fill="black"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* ✅ Preview selected attachments */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 pl-12">
              {attachments.map((a, i) => {
                const fileName = a.file_name || "File";
                const fileUrl = a.file_url || a.url || "#";
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1 text-sm shadow-sm"
                  >
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate max-w-[150px] text-slate-700 hover:text-blue-600 hover:underline cursor-pointer"
                      title={fileName}
                    >
                      {fileName}
                    </a>
                    <button
                      onClick={async () => {
                        const fileId = a.id;
                        setAttachments((prev) =>
                          prev.filter((_, idx) => idx !== i)
                        );
                        try {
                          await apiCall({
                            method: "post",
                            url: `attachments/delete-unconfirmed-attachment/${fileId}`,
                          });
                        } catch (err) {
                          console.error(
                            "Error deleting unconfirmed file:",
                            err
                          );
                        }
                      }}
                      className="text-slate-400 hover:text-red-500 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="bg-[#E1E4EA] h-[1px] w-full"></div>
        </div>
      </div>
    </div>
  );
};

export default CommentsSection;
